import { Stack, useTheme } from "@mui/material";
import { FileText } from "lucide-react";
import { useIsViewer } from "web_myplann/Api/Rights/RightsProvider";
import type {
	ClientPortfolioDomainEnum,
	ClientPropertyTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { PortfolioItemIcon } from "web_myplann/Portfolio/Components/PortfolioItemIcon";
import { DetailActionRow } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailActionRow";
import { DetailCard } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailCard";
import type { PinKind } from "web_myplann/Portfolio/Components/Redesign/Detail/Pin/pinKind";
import { PinInlineAction } from "web_myplann/Portfolio/Components/Redesign/Detail/PinInlineAction";
import { RelatedEmptyHint } from "web_myplann/Portfolio/Components/Redesign/Detail/RelatedEmptyHint";
import { IconBox } from "web_myplann/Shared/Components/IconBox";

const ICON_BOX_SIZE = 36;
const ICON_GLYPH_SIZE = 18;

export type RelatedItemIconSource = {
	domain?: ClientPortfolioDomainEnum | null;
	propertyType?: ClientPropertyTypeEnum | null;
};

type Props<TItem extends { id: number }> = {
	kind: PinKind;
	items: TItem[];
	getLabel: (item: TItem) => string;
	getIconSource?: (item: TItem) => RelatedItemIconSource;
	getSubtitle?: (item: TItem) => string;
	onItemClick: (item: TItem) => void;
	onPin: (kind: PinKind) => void;
};

export const RelatedCard = <TItem extends { id: number }>({
	kind,
	items,
	getLabel,
	getIconSource,
	getSubtitle,
	onItemClick,
	onPin,
}: Props<TItem>) => {
	const { t } = useResource();
	const { palette } = useTheme();
	const isViewer = useIsViewer();

	const kindResources = Resources.DataCard.RelatedCard[kind];
	const hasItems = items.length > 0;

	return (
		<DetailCard label={t(kindResources.title)}>
			<Stack>
				{hasItems ? (
					items.map((item) => (
						<DetailActionRow
							key={item.id}
							truncate
							icon={
								getIconSource ? (
									<PortfolioItemIcon
										{...getIconSource(item)}
										size={ICON_BOX_SIZE}
										glyphSize={ICON_GLYPH_SIZE}
									/>
								) : (
									<IconBox bg={palette.grey[100]} size={ICON_BOX_SIZE}>
										<FileText
											size={ICON_GLYPH_SIZE}
											color={palette.grey[500]}
										/>
									</IconBox>
								)
							}
							label={getLabel(item)}
							subtitle={getSubtitle?.(item)}
							onClick={() => onItemClick(item)}
						/>
					))
				) : (
					<RelatedEmptyHint text={t(kindResources.empty)} />
				)}
				{!isViewer && (
					<PinInlineAction
						label={t(hasItems ? kindResources.edit : kindResources.pin)}
						onClick={() => onPin(kind)}
					/>
				)}
			</Stack>
		</DetailCard>
	);
};
